import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import Header from '../components/layout/Header'
import { REFERRALS_SEO } from '../config/seo'
import { useWalletAuth } from '../context/WalletAuthProvider'
import { usePageCss } from '../hooks/usePageCss'
import { usePageMeta } from '../hooks/usePageMeta'
import { signalCellOpacity } from '../components/trade/positionsEmpty'
import './referrals-mobile.css'

const RANGE_OPTIONS = [
  { key: '1d', label: '24H' },
  { key: '1w', label: '1W' },
  { key: '1m', label: '1M' },
  { key: '3m', label: '3M' },
  { key: '6m', label: '6M' },
  { key: 'all-time', label: 'All-time' },
]

const ITEM_BASE =
  'flex w-full cursor-pointer items-center justify-between gap-2 rounded-control p-2 text-left text-sm/sm text-ink transition-colors outline-none bg-transparent hover:bg-ink/5 data-highlighted:bg-ink/5 data-disabled:opacity-50 data-disabled:bg-transparent'

const CONNECT_CLASS =
  'group inline-flex w-fit shrink-0 cursor-pointer items-center justify-center truncate rounded-pill text-sm/sm font-medium transition-all active:scale-[0.97] disabled:cursor-auto disabled:opacity-50 disabled:active:scale-100 bg-ink text-bg shadow-border-button-primary hover:bg-ink/90 active:bg-ink/80 disabled:hover:bg-ink disabled:active:bg-ink light:shadow-(--btn-primary-shadow) h-8 gap-2 p-2 max-mobile:min-h-(--control-h-lg) rounded-card px-4'

const EMPTY_PANEL_CLASS =
  "flex flex-col items-center gap-5 rounded-panel border-[0.5px] border-line bg-overlay px-6 py-20 text-center shadow-[inset_0_var(--hl-y)_0_0_var(--hl-top),0px_0px_0px_0.5px_var(--ring-crisp),0px_1px_0px_0px_var(--edge-under),var(--shadow-sm)] max-mobile:py-14 light:border-transparent light:bg-overlay light:shadow-[0_0_0_0.5px_var(--ring-crisp),0_1px_0_0_var(--edge-under),var(--shadow-sm)]"

function openConnect() {
  window.dispatchEvent(new Event('lighter:open-connect'))
}

function CheckIcon({ visible }) {
  return (
    <span
      data-icon="true"
      className={`flex size-3 shrink-0 items-center justify-center text-ink ${
        visible ? 'opacity-85' : 'opacity-0'
      }`}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 256 256"
        fill="currentColor"
        className="size-full"
      >
        <path d="M229.66,77.66l-128,128a8,8,0,0,1-11.32,0l-56-56a8,8,0,0,1,11.32-11.32L96,188.69,218.34,66.34a8,8,0,0,1,11.32,11.32Z" />
      </svg>
    </span>
  )
}

function InfoIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <circle cx="7" cy="7" r="5.5" stroke="currentColor" strokeWidth="1.25" />
      <path d="M7 6.2V10" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" />
      <circle cx="7" cy="4.2" r="0.7" fill="currentColor" />
    </svg>
  )
}

function StatCard({ label, value, hint, valueClass, caption }) {
  return (
    <div className="flex min-w-0 flex-col gap-2.5">
      <div className="flex items-center gap-1.5 font-mono text-md font-medium tracking-caps text-muted uppercase">
        <span className="[text-box-edge:cap_alphabetic] [text-box-trim:trim-both]">
          {label}
        </span>
        {hint ? (
          <button
            type="button"
            aria-label={hint}
            className="inline-grid size-3.5 cursor-help place-items-center rounded-full text-muted transition-colors hover:text-ink focus-visible:[outline:2px_solid_var(--color-ink)] focus-visible:outline-offset-2"
            data-state="closed"
          >
            <InfoIcon />
          </button>
        ) : null}
      </div>
      <span
        className={`text-5xl leading-none font-semibold tracking-display tabular-nums max-tablet:text-4xl max-mobile:text-3xl ${valueClass}`}
      >
        {value}
      </span>
      {caption ? (
        <span className="text-base text-muted tabular-nums">{caption}</span>
      ) : null}
    </div>
  )
}

function RangeMenu({ selected, onSelect, anchorRef }) {
  const [pos, setPos] = useState(null)

  useLayoutEffect(() => {
    const el = anchorRef?.current
    if (!el) return undefined
    const update = () => {
      const rect = el.getBoundingClientRect()
      const width = 160
      let left = rect.right - width
      if (left < 8) left = 8
      if (left + width > window.innerWidth - 8) {
        left = Math.max(8, window.innerWidth - width - 8)
      }
      setPos({
        top: rect.bottom + 6,
        left,
      })
    }
    update()
    window.addEventListener('resize', update)
    window.addEventListener('scroll', update, true)
    return () => {
      window.removeEventListener('resize', update)
      window.removeEventListener('scroll', update, true)
    }
  }, [anchorRef])

  if (!pos) return null

  return createPortal(
    <div
      data-radix-popper-content-wrapper=""
      dir="ltr"
      style={{
        position: 'fixed',
        left: pos.left,
        top: pos.top,
        minWidth: 'max-content',
        zIndex: 9999,
        willChange: 'transform',
      }}
    >
      <div
        data-testid="referrals-range-menu"
        data-side="bottom"
        data-align="end"
        data-state="open"
        data-dropdown-menu-content="true"
        role="menu"
        aria-orientation="vertical"
        tabIndex={-1}
        dir="ltr"
        className="referrals-range-menu z-9999 max-h-96 min-w-[max(10rem,var(--radix-dropdown-menu-trigger-width))] origin-(--radix-dropdown-menu-content-transform-origin) overflow-y-scroll rounded-card p-1 shadow-elevate-popover anim-picker-in border-0 bg-elevated light:bg-overlay"
        style={{ outline: 'none', '--radix-dropdown-menu-trigger-width': '68.703125px' }}
      >
        {RANGE_OPTIONS.map((option) => {
          const isSelected = option.key === selected
          return (
            <div
              key={option.key}
              role="menuitem"
              tabIndex={-1}
              data-orientation="vertical"
              className={isSelected ? `${ITEM_BASE} font-medium text-ink` : ITEM_BASE}
              onClick={() => onSelect(option.key)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault()
                  onSelect(option.key)
                }
              }}
            >
              <div className="flex items-center gap-2">
                <span>{option.label}</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckIcon visible={isSelected} />
              </div>
            </div>
          )
        })}
      </div>
    </div>,
    document.body
  )
}

function DisconnectedPanel() {
  const patternId = useId().replace(/:/g, '')
  const [cells, setCells] = useState([])

  useEffect(() => {
    const started = performance.now()
    const period = 2600
    let raf = 0
    const tick = (now) => {
      const cycle = ((now - started) % period) / period
      const ping = cycle < 0.5 ? cycle * 2 : 2 - cycle * 2
      const t = ping * ping * (3 - 2 * ping)
      const next = []
      for (let col = 0; col < 11; col += 1) {
        for (let row = 0; row < 11; row += 1) {
          const op = signalCellOpacity(col, row, t)
          if (!op) continue
          next.push({
            key: `${col}-${row}`,
            x: 0.65 + col * 4,
            y: 0.65 + row * 4,
            op,
          })
        }
      }
      setCells(next)
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [])

  return (
    <div role="status" className={EMPTY_PANEL_CLASS} style={{ transform: 'none' }}>
      <span className="inline-flex">
        <svg
          viewBox="0 0 44 44"
          className="size-20 max-mobile:size-14 overflow-visible"
          aria-hidden="true"
        >
          <defs>
            <pattern
              id={patternId}
              width="4"
              height="4"
              patternUnits="userSpaceOnUse"
            >
              <rect
                x="0.65"
                y="0.65"
                width="2.7"
                height="2.7"
                fill="currentColor"
                fillOpacity="0.09"
              />
            </pattern>
          </defs>
          <rect width="44" height="44" fill={`url(#${patternId})`} className="text-ink" />
          <g data-positions-signal-bars>
            {cells.map((cell) => (
              <rect
                key={cell.key}
                x={cell.x}
                y={cell.y}
                width="2.7"
                height="2.7"
                fill="currentColor"
                fillOpacity={cell.op}
              />
            ))}
          </g>
        </svg>
      </span>
      <div className="flex flex-col items-center gap-2">
        <span className="font-mono label-meta text-lg/lg text-ink">Not connected</span>
        <p className="max-w-[420px] text-md/md text-pretty text-dim">
          Connect a wallet to see your referral code and earnings.
        </p>
      </div>
      <button type="button" className={CONNECT_CLASS} onClick={openConnect}>
        <span>Connect Wallet</span>
      </button>
    </div>
  )
}

export default function Referrals() {
  usePageMeta(REFERRALS_SEO)
  usePageCss('referrals-page-css', '/css/lighter_referrals.css')
  const { isConnected } = useWalletAuth()
  const [range, setRange] = useState('all-time')
  const [rangeOpen, setRangeOpen] = useState(false)
  const rangeTriggerRef = useRef(null)

  useEffect(() => {
    const onPointer = (event) => {
      if (
        event.target.closest(
          '[data-testid="referrals-range-menu"], [data-testid="referrals-range-trigger"]'
        )
      ) {
        return
      }
      setRangeOpen(false)
    }
    const onKey = (event) => {
      if (event.key === 'Escape') setRangeOpen(false)
    }
    document.addEventListener('pointerdown', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [])

  const rangeLabel = RANGE_OPTIONS.find((option) => option.key === range)?.label

  return (
    <div className="referrals-page flex h-dvh w-full flex-col overflow-hidden bg-bg text-ink">
      <Header />
      <div
        data-testid="sub-page-scroll-region"
        className="min-h-0 flex-1 overflow-y-auto"
        style={{ '--fade-t': '0px', '--fade-b': '0px' }}
      >
        <div className="mx-auto w-full max-w-[1240px] px-8 py-10 max-mobile:px-4 max-mobile:py-6">
          <section className="section-in max-mobile:mb-6 mb-8" style={{ '--sec-d': '0s' }}>
            <div className="flex items-end justify-between gap-6 max-mobile:flex-col max-mobile:items-start max-mobile:gap-4">
              <div className="min-w-0">
                <div className="flex min-w-0 flex-wrap items-center gap-3">
                  <h1 className="m-0 text-hero leading-[1.02] font-medium tracking-display [text-box-edge:cap_alphabetic] [text-box-trim:trim-start]">
                    Referrals
                  </h1>
                </div>
              </div>
              <div className="relative shrink-0 max-mobile:w-full">
                <button
                  type="button"
                  ref={rangeTriggerRef}
                  id="referrals-range-trigger"
                  data-testid="referrals-range-trigger"
                  aria-haspopup="menu"
                  aria-expanded={rangeOpen}
                  data-state={rangeOpen ? 'open' : 'closed'}
                  className="group inline-flex items-center justify-between text-sm/sm transition-colors disabled:opacity-50 gap-1 relative shrink-0 cursor-pointer rounded-control py-1 pr-1 pl-1.5 text-dim hover:bg-ink/5 hover:text-ink data-[state=open]:bg-ink/5 data-[state=open]:text-ink max-mobile:before:absolute max-mobile:before:inset-x-0 max-mobile:before:-inset-y-[6px] max-mobile:before:content-['']"
                  onClick={() => setRangeOpen((open) => !open)}
                >
                  <div className="flex items-center gap-1">
                    <span className="whitespace-nowrap">{rangeLabel}</span>
                  </div>
                  <span
                    data-icon="true"
                    className="flex shrink-0 items-center justify-center rotate-0 transition-all group-data-[state=open]:rotate-180 size-[12px] text-muted"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 256 256"
                      fill="currentColor"
                      className="size-full"
                    >
                      <path d="M213.66,101.66l-80,80a8,8,0,0,1-11.32,0l-80-80A8,8,0,0,1,53.66,90.34L128,164.69l74.34-74.35a8,8,0,0,1,11.32,11.32Z" />
                    </svg>
                  </span>
                </button>
                {rangeOpen ? (
                  <RangeMenu
                    selected={range}
                    anchorRef={rangeTriggerRef}
                    onSelect={(key) => {
                      setRange(key)
                      setRangeOpen(false)
                    }}
                  />
                ) : null}
              </div>
            </div>
            <div className="mt-4 max-w-[60%] text-xl/2xl text-pretty text-dim max-mobile:max-w-none">
              Invite traders and earn up to 30% of the fees they pay, settled in USDC
              every Monday. Read more on our{' '}
              <a
                target="_blank"
                rel="noopener noreferrer"
                className="text-link underline hover:text-link"
                href="https://docs.lighter.xyz/referral-program"
              >
                docs
              </a>
              .
            </div>
          </section>

          <section
            className="section-in grid gap-x-12 gap-y-8 grid-cols-4 max-tablet:grid-cols-2 max-mobile:gap-x-6 max-mobile:gap-y-6"
            style={{ '--sec-d': '0.12s' }}
          >
            <StatCard
              label="Kickback Earned"
              value="$0.00"
              valueClass="text-up-strong"
              caption="10% of referral fees"
              hint="10% of fees paid by accounts you referred. Paid in USDC every next Monday."
            />
            <StatCard
              label="Referral Fees Paid"
              value="$0.00"
              valueClass="text-ink"
              hint="Fees generated by accounts you referred within the selected window."
            />
            <StatCard
              label="Referral Volume"
              value="$0.00"
              valueClass="text-ink"
              hint="Trading volume generated by accounts you referred within the selected window."
            />
            <StatCard label="Referred" value="0" valueClass="text-ink" />
          </section>

          <div className="mt-3">
            {isConnected ? null : <DisconnectedPanel />}
          </div>
        </div>
      </div>
    </div>
  )
}
